import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { 
  MapPin, Plus, X, Clock, Car, Users, Cloud, 
  Sun, Camera, Tag, Info, Navigation, Crosshair,
  ChevronRight, ChevronLeft, Check, Loader2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MultiSelectBadges, SingleSelectBadges } from '../ui/multi-select-badges';
import {
  WEATHER_OPTIONS, LIGHT_OPTIONS, CROWD_LEVEL_OPTIONS,
  DIFFICULTY_OPTIONS, TIME_WINDOW_OPTIONS
} from '../../constants/spotEnums';
import ImageUploader from '../ImageUpload/ImageUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom markers
const spotIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const parkingIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});


// Steps configuration
const STEPS = [
  { id: 'location', title: 'Localisation', icon: MapPin, description: 'Positionnez le spot' },
  { id: 'info', title: 'Informations', icon: Info, description: 'Titre et description' },
  { id: 'conditions', title: 'Conditions', icon: Sun, description: 'Météo et lumière idéales' },
  { id: 'timing', title: 'Horaires', icon: Clock, description: 'Meilleurs moments' },
  { id: 'access', title: 'Accès', icon: Car, description: 'Parking et accessibilité' },
  { id: 'photos', title: 'Photos', icon: Camera, description: 'Ajoutez des photos' },
];


// Map click handler component
function MapClickHandler({ onSpotClick, onParkingClick, mode }) {
  useMapEvents({
    click(e) {
      if (mode === 'spot') {
        onSpotClick([e.latlng.lat, e.latlng.lng]);
      } else if (mode === 'parking') {
        onParkingClick([e.latlng.lat, e.latlng.lng]);
      }
    },
  });
  return null;
}


export default function AddSpotModalV2({ isOpen, onClose, initialPosition }) {
  const { user, token } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mapMode, setMapMode] = useState('spot'); // 'spot' or 'parking'
  
  // Form state - matching new data model
  const [formData, setFormData] = useState({
    // Required
    title: '',
    description: '',
    spot_lat: null,
    spot_lng: null,
    
    // Recommended time windows
    recommended_time_windows: [],
    
    // Ideal conditions
    ideal_conditions: {
      weather: [],
      light: [],
      notes: ''
    },
    
    // Crowd level
    crowd_level: 'moderate',
    
    // Accessibility
    accessibility: {
      difficulty: 'easy',
      walk_time_minutes: null,
      access_notes: ''
    },
    
    // Parking
    parking: {
      parking_lat: null,
      parking_lng: null,
      parking_distance_m: null,
      parking_info: ''
    },
    
    // Tags
    tags: [],
    
    // Photos (URLs après upload)
    photos: []
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setFormData({
        title: '',
        description: '',
        spot_lat: initialPosition?.[0] || null,
        spot_lng: initialPosition?.[1] || null,
        recommended_time_windows: [],
        ideal_conditions: { weather: [], light: [], notes: '' },
        crowd_level: 'moderate',
        accessibility: { difficulty: 'easy', walk_time_minutes: null, access_notes: '' },
        parking: { parking_lat: null, parking_lng: null, parking_distance_m: null, parking_info: '' },
        tags: [],
        photos: []
      });
    }
  }, [isOpen, initialPosition]);

  // Update form data
  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Update nested form data
  const updateNestedFormData = useCallback((parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  }, []);

  // Handle spot position
  const handleSpotClick = useCallback((position) => {
    updateFormData('spot_lat', position[0]);
    updateFormData('spot_lng', position[1]);
  }, [updateFormData]);

  // Handle parking position
  const handleParkingClick = useCallback((position) => {
    updateNestedFormData('parking', 'parking_lat', position[0]);
    updateNestedFormData('parking', 'parking_lng', position[1]);
  }, [updateNestedFormData]);

  // Toggle time window
  const toggleTimeWindow = (type) => {
    const existing = formData.recommended_time_windows.find(tw => tw.type === type);
    if (existing) {
      updateFormData('recommended_time_windows', 
        formData.recommended_time_windows.filter(tw => tw.type !== type)
      );
    } else {
      updateFormData('recommended_time_windows', 
        [...formData.recommended_time_windows, { type, notes: '' }]
      );
    }
  };

  // Handle tag input
  const [tagInput, setTagInput] = useState('');
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim().toLowerCase())) {
      updateFormData('tags', [...formData.tags, tagInput.trim().toLowerCase()]);
      setTagInput('');
    }
  };
  const removeTag = (tag) => {
    updateFormData('tags', formData.tags.filter(t => t !== tag));
  };

  // Validation
  const isStepValid = (step) => {
    switch (step) {
      case 0: // Location
        return formData.spot_lat && formData.spot_lng;
      case 1: // Info
        return formData.title.trim().length >= 3 && formData.description.trim().length >= 10;
      case 2: // Conditions
        return true; // Optional
      case 3: // Timing
        return true; // Optional
      case 4: // Access
        return true; // Optional
      case 5: // Photos
        return true; // Optional
      default:
        return true;
    }
  };

  // Submit
  const handleSubmit = async () => {
    if (!user || !token) {
      alert('Vous devez être connecté pour créer un spot');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/spots-v2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la création');
      }

      const newSpot = await response.json();
      console.log('Spot créé:', newSpot);
      onClose(true); // Close with success flag
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Navigation
  const goNext = () => {
    if (currentStep < STEPS.length - 1 && isStepValid(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goPrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const mapCenter = formData.spot_lat && formData.spot_lng 
    ? [formData.spot_lat, formData.spot_lng]
    : initialPosition || [46.8, 2.3]; // France center

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="p-4 border-b bg-gradient-to-r from-orange-500 to-orange-600">
          <DialogTitle className="text-white flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Ajouter un nouveau spot
          </DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-b overflow-x-auto">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            const isValid = isStepValid(index);
            
            return (
              <button
                key={step.id}
                onClick={() => index <= currentStep && setCurrentStep(index)}
                disabled={index > currentStep}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all whitespace-nowrap ${
                  isActive 
                    ? 'bg-orange-100 text-orange-700' 
                    : isCompleted 
                      ? 'text-green-600' 
                      : 'text-gray-400'
                } ${index <= currentStep ? 'cursor-pointer hover:bg-gray-100' : 'cursor-not-allowed'}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isActive 
                    ? 'bg-orange-500 text-white' 
                    : isCompleted 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-200'
                }`}>
                  {isCompleted ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </div>
                <span className="hidden sm:inline text-sm font-medium">{step.title}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 0: Location */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">Localisation du spot</h3>
                  <p className="text-sm text-gray-500">Cliquez sur la carte pour positionner le spot</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={mapMode === 'spot' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMapMode('spot')}
                    className={mapMode === 'spot' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  >
                    <MapPin className="h-4 w-4 mr-1" />
                    Spot
                  </Button>
                  <Button
                    variant={mapMode === 'parking' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMapMode('parking')}
                    className={mapMode === 'parking' ? 'bg-blue-500 hover:bg-blue-600' : ''}
                  >
                    <Car className="h-4 w-4 mr-1" />
                    Parking
                  </Button>
                </div>
              </div>

              <div className="h-[400px] rounded-lg overflow-hidden border">
                <MapContainer
                  center={mapCenter}
                  zoom={formData.spot_lat ? 14 : 6}
                  className="h-full w-full"
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapClickHandler
                    onSpotClick={handleSpotClick}
                    onParkingClick={handleParkingClick}
                    mode={mapMode}
                  />
                  {formData.spot_lat && formData.spot_lng && (
                    <Marker position={[formData.spot_lat, formData.spot_lng]} icon={spotIcon} />
                  )}
                  {formData.parking.parking_lat && formData.parking.parking_lng && (
                    <Marker position={[formData.parking.parking_lat, formData.parking.parking_lng]} icon={parkingIcon} />
                  )}
                </MapContainer>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-orange-50 rounded-lg">
                  <p className="text-xs text-orange-600 font-medium mb-1">📍 Position du spot</p>
                  {formData.spot_lat ? (
                    <p className="text-sm">{formData.spot_lat.toFixed(5)}, {formData.spot_lng.toFixed(5)}</p>
                  ) : (
                    <p className="text-sm text-gray-400">Non définie</p>
                  )}
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium mb-1">🅿️ Position du parking</p>
                  {formData.parking.parking_lat ? (
                    <p className="text-sm">{formData.parking.parking_lat.toFixed(5)}, {formData.parking.parking_lng.toFixed(5)}</p>
                  ) : (
                    <p className="text-sm text-gray-400">Optionnel - Non défini</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <Label htmlFor="title" className="text-sm font-medium">
                  Nom du spot *
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => updateFormData('title', e.target.value)}
                  placeholder="Ex: Belvédère du Mont Salève"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-sm font-medium">
                  Description *
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => updateFormData('description', e.target.value)}
                  placeholder="Décrivez le spot, ce qu'on peut y voir, l'ambiance..."
                  rows={4}
                  className="mt-1"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {formData.description.length} caractères (minimum 10)
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium">Tags</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Ajouter un tag..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" onClick={addTag} variant="outline">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map(tag => (
                      <span 
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm"
                      >
                        #{tag}
                        <button onClick={() => removeTag(tag)} className="text-gray-400 hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Ideal Conditions */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <MultiSelectBadges
                options={WEATHER_OPTIONS}
                selected={formData.ideal_conditions.weather}
                onChange={(values) => updateNestedFormData('ideal_conditions', 'weather', values)}
                label="Météo idéale"
                description="Dans quelles conditions météo ce spot est-il le plus beau ?"
              />

              <MultiSelectBadges
                options={LIGHT_OPTIONS}
                selected={formData.ideal_conditions.light}
                onChange={(values) => updateNestedFormData('ideal_conditions', 'light', values)}
                label="Lumière idéale"
                description="Quelle lumière met le mieux en valeur ce spot ?"
              />

              <div>
                <Label className="text-sm font-medium">Notes sur les conditions (optionnel)</Label>
                <Textarea
                  value={formData.ideal_conditions.notes}
                  onChange={(e) => updateNestedFormData('ideal_conditions', 'notes', e.target.value)}
                  placeholder="Ex: Magnifique après un orage quand le ciel se dégage..."
                  rows={2}
                  className="mt-1"
                />
              </div>

              <SingleSelectBadges
                options={CROWD_LEVEL_OPTIONS}
                selected={formData.crowd_level}
                onChange={(value) => updateFormData('crowd_level', value)}
                label="Niveau d'affluence habituel"
                description="En général, y a-t-il beaucoup de monde ?"
              />
            </div>
          )}

          {/* Step 3: Timing */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium">Meilleurs moments pour visiter</Label>
                <p className="text-xs text-gray-500 mb-3">Sélectionnez les créneaux horaires recommandés</p>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {TIME_WINDOW_OPTIONS.map((option) => {
                    const isSelected = formData.recommended_time_windows.some(tw => tw.type === option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleTimeWindow(option.value)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-2xl">{option.icon}</span>
                        <p className="font-medium text-sm mt-1">{option.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {formData.recommended_time_windows.length > 0 && (
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm font-medium text-orange-700 mb-2">
                    {formData.recommended_time_windows.length} créneau(x) sélectionné(s)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.recommended_time_windows.map(tw => {
                      const option = TIME_WINDOW_OPTIONS.find(o => o.value === tw.type);
                      return (
                        <span key={tw.type} className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-full text-sm">
                          {option?.icon} {option?.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Access */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <SingleSelectBadges
                options={DIFFICULTY_OPTIONS}
                selected={formData.accessibility.difficulty}
                onChange={(value) => updateNestedFormData('accessibility', 'difficulty', value)}
                label="Difficulté d'accès"
                description="Comment évaluez-vous l'accès au spot ?"
              />

              <div>
                <Label className="text-sm font-medium">Temps de marche (minutes)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.accessibility.walk_time_minutes || ''}
                  onChange={(e) => updateNestedFormData('accessibility', 'walk_time_minutes', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Ex: 15"
                  className="mt-1 w-32"
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Notes sur l'accès</Label>
                <Textarea
                  value={formData.accessibility.access_notes}
                  onChange={(e) => updateNestedFormData('accessibility', 'access_notes', e.target.value)}
                  placeholder="Ex: Sentier balisé depuis le parking, prévoir de bonnes chaussures..."
                  rows={2}
                  className="mt-1"
                />
              </div>

              <div className="border-t pt-6">
                <h4 className="font-medium mb-4">🅿️ Informations parking</h4>
                
                <div>
                  <Label className="text-sm font-medium">Informations sur le parking</Label>
                  <Textarea
                    value={formData.parking.parking_info}
                    onChange={(e) => updateNestedFormData('parking', 'parking_info', e.target.value)}
                    placeholder="Ex: Parking gratuit, 20 places. Payant en été (2€/h)..."
                    rows={2}
                    className="mt-1"
                  />
                </div>

                {formData.parking.parking_lat && (
                  <p className="text-sm text-green-600 mt-2">
                    ✓ Position du parking définie sur la carte
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Photos */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Photos du spot</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Ajoutez des photos pour illustrer le spot. Les images sont compressées automatiquement.
                </p>
                
                <ImageUploader
                  category="spots"
                  maxImages={10}
                  existingImages={formData.photos}
                  onImagesUploaded={(newPhotos) => {
                    updateFormData('photos', [...formData.photos, ...newPhotos]);
                  }}
                />
              </div>

              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">✓ Récapitulatif</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>📍 Position: {formData.spot_lat?.toFixed(4)}, {formData.spot_lng?.toFixed(4)}</li>
                  <li>📝 {formData.title}</li>
                  <li>🏷️ {formData.tags.length} tag(s)</li>
                  <li>☀️ {formData.ideal_conditions.weather.length} condition(s) météo</li>
                  <li>🕐 {formData.recommended_time_windows.length} créneau(x) horaire(s)</li>
                  <li>📷 {formData.photos.length} photo(s)</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <Button
            variant="outline"
            onClick={goPrev}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Précédent
          </Button>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              Étape {currentStep + 1} / {STEPS.length}
            </span>
          </div>

          {currentStep < STEPS.length - 1 ? (
            <Button
              onClick={goNext}
              disabled={!isStepValid(currentStep)}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Suivant
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !isStepValid(currentStep)}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Créer le spot
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
